import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';

// Pages
import HomePage from './pages/HomePage';
import PeoplePage from './pages/PeoplePage';
import PersonDetailPage from './pages/PersonDetailPage';
import ExplorerPage from './pages/ExplorerPage';
import MediaPage from './pages/MediaPage';
import MediaDetailPage from './pages/MediaDetailPage';
import AnalyzerPage from './pages/AnalyzerPage';
import CommunityPage from './pages/CommunityPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import StoryDetailPage from './pages/StoryDetailPage';
import BookmarksPage from './pages/BookmarksPage';
import AuthPage from './pages/AuthPage';
import AuthCallback from './components/AuthCallback';
import ProfilePage from './pages/ProfilePage';
import ReferralPage from './pages/ReferralPage';

// Components
import Navbar from './components/Navbar';
import GrainOverlay from './components/GrainOverlay';

import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set document title
const APP_TITLE = "Res Tota Ipsum — The Whole of It Laid Out Before You";

// Custom hook for page titles
export const usePageTitle = (title) => {
  useEffect(() => {
    document.title = title ? `${title} | Res Tota Ipsum` : APP_TITLE;
    return () => {
      document.title = APP_TITLE;
    };
  }, [title]);
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: Skip auth check if returning from OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = (userData, token) => {
    setUser(userData);
    if (token) {
      localStorage.setItem('token', token);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error(e);
    }
    localStorage.removeItem('token');
    setUser(null);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  
  // Synchronous check for session_id in URL fragment before rendering routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <GrainOverlay />
      <main className="pt-16">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/people" element={<PeoplePage />} />
          <Route path="/people/:personId" element={<PersonDetailPage />} />
          <Route path="/explorer" element={<ExplorerPage />} />
          <Route path="/media" element={<MediaPage />} />
          <Route path="/media/:mediaId" element={<MediaDetailPage />} />
          <Route path="/analyzer" element={<AnalyzerPage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
          <Route path="/story/:storyId" element={<StoryDetailPage />} />
          <Route path="/bookmarks" element={<BookmarksPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/referral" element={<ReferralPage />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster 
          position="bottom-right" 
          theme="dark"
          toastOptions={{
            style: {
              background: '#0A0A0A',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#EDEDED'
            }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export { API, BACKEND_URL };
export default App;
